/**
 * Theme management.
 *
 * EFS defaults to dark (per design reference §4). User choice persists in
 * `localStorage` under `efs.theme`. The class is applied to the
 * `<html>` element so Tailwind `darkMode: 'class'` picks it up.
 */

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'efs.theme';
const FIELD_CONTRAST_STORAGE_KEY = 'efs.fieldContrast';

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage ?? null;
  } catch {
    // Safari private mode or sandboxed iframe can throw on access.
    return null;
  }
}

export function getStoredTheme(): ThemeMode | null {
  const store = safeStorage();
  if (!store) return null;
  try {
    const v = store.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

export function resolveInitialTheme(): ThemeMode {
  return getStoredTheme() ?? 'dark';
}

export function applyTheme(mode: ThemeMode): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.classList.toggle('light', mode === 'light');
}

export function getStoredFieldContrast(): boolean {
  const store = safeStorage();
  if (!store) return false;
  try {
    return store.getItem(FIELD_CONTRAST_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function applyFieldContrast(enabled: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('field-contrast', enabled);
}

export function setFieldContrast(enabled: boolean): void {
  const store = safeStorage();
  if (store) {
    try {
      store.setItem(FIELD_CONTRAST_STORAGE_KEY, enabled ? 'true' : 'false');
    } catch {
      // Quota exceeded or read-only — non-fatal.
    }
  }
  applyFieldContrast(enabled);
}

export function setTheme(mode: ThemeMode): void {
  const store = safeStorage();
  if (store) {
    try {
      store.setItem(STORAGE_KEY, mode);
    } catch {
      // Quota exceeded or read-only — non-fatal.
    }
  }
  applyTheme(mode);
}

export function toggleTheme(current: ThemeMode): ThemeMode {
  const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}

export function toggleFieldContrast(current: boolean): boolean {
  const next = !current;
  setFieldContrast(next);
  return next;
}
